
import { useEffect, useState } from "react";
import Title from "../../../componente/cadastro/titulo/index.jsx";
import Description from "../../../componente/cadastro/descricao/index.jsx";
import Table from "../../../componente/cadastro/tabela/index.jsx";
import Pagination from "../../../componente/cadastro/paginacao/index.jsx";
import Sidebar from "../../../componente/sidebar/index.jsx";
import "./style.css";
import api from "../../../services/api.js";
import { useAppContext } from "../../context/AppContext.jsx";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import Impressora from "../../../assets/Impressora.png";
import ImgXLS from "../../../assets/XLS.png";
import { usePermissoes } from "../../../componente/permissoes.js";

const RelPedidos = () => {
    const { podeImprimir } = usePermissoes();

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 0,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { accessKey, codassinante, usuario, codempresa } = useAppContext();

    const getFirstDayOfMonth = () => {
        const dt = new Date();
        dt.setMonth(dt.getMonth());

        return `${dt.getFullYear()}-${(dt.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-01`;
    };

    const getCurrentDate = () => {
        const dt = new Date();
        dt.setMonth(dt.getMonth());

        return `${dt.getFullYear()}-${(dt.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${dt.getDate()
                .toString()
                .padStart(2, "0")}`;
    };

    // Filtros
    const [periodo, setPeriodo] = useState("Do Dia");
    const [valorPeriodo, setValorPeriodo] = useState(getCurrentDate());
    const [dataInicio, setDataInicio] = useState(getFirstDayOfMonth());
    const [dataFim, setDataFim] = useState(getCurrentDate());
    const [agruparPor, setAgruparPor] = useState("Vendedor");
    const [cidade, setCidade] = useState("");
    const [vendedor, setVendedor] = useState("");

    // Novo filtro de origem:
    // T = Todos | P = Pronta Entrega | E = Encomenda
    const [origem, setOrigem] = useState("T");

    // Colunas dinâmicas
    const getColumns = () => {
        switch (agruparPor) {
            case "Cliente":
                return [
                    { key: "cliente", title: "Cliente" },
                    { key: "valor", title: "Valor", type: "float" },
                    { key: "frete", title: "Frete", type: "float" },
                    { key: "desconto", title: "Desconto", type: "float" },
                    { key: "totalvenda", title: "Total Venda", type: "float" },
                ];

            case "Produto":
                return [
                    { key: "sku", title: "SKU" },
                    { key: "produto", title: "Produto" },
                    { key: "qtde", title: "Qtde", type: "number" },
                    { key: "precomedio", title: "Preço Médio", type: "float" },
                    { key: "total", title: "Total Venda", type: "float" },
                ];

            case "Vendedor":
                return [
                    { key: "vendedor", title: "Vendedor" },
                    { key: "valor", title: "Valor", type: "float" },
                    { key: "frete", title: "Frete", type: "float" },
                    { key: "desconto", title: "Desconto", type: "float" },
                    { key: "totalvenda", title: "Total Venda", type: "float" },
                ];

            default:
                return [
                    { key: "nomefantasia", title: "Empresa" },
                    { key: "valor", title: "Valor", type: "float" },
                    { key: "frete", title: "Frete", type: "float" },
                    { key: "desconto", title: "Desconto", type: "float" },
                    { key: "totalvenda", title: "Total Venda", type: "float" },
                ];
        }
    };

    const columns = getColumns();

    // Parâmetros comuns aos relatórios
    const getReportParams = (incluirPaginacao = false) => {
        let dataParaAPI = valorPeriodo;

        if (valorPeriodo && valorPeriodo.length === 7) {
            dataParaAPI = `${valorPeriodo}-01`;
        }

        const params = {
            codassinante,
            periodo,
            agrupar_por: agruparPor,
            cidade,
            vendedor,
            codempresa,

            // O filtro só tem efeito quando o agrupamento é Produto.
            origem: agruparPor === "Produto" ? origem : "T",
        };

        if (incluirPaginacao) {
            params.page = currentPage;
            params.per_page = perPage;
        }

        if (periodo === "Do Dia") {
            params.data = dataParaAPI;
        }

        if (periodo === "Do Mês") {
            params.mes = dataParaAPI;
        }

        if (periodo === "De um período") {
            params.data_inicio = dataInicio;
            params.data_fim = dataFim;
        }

        return params;
    };

    // Consulta dos dados
    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = getReportParams(true);

            const response = await api.get("/v1/relatorio/pedido", {
                params,
                headers: {
                    Authorization: `Bearer ${accessKey}`,
                },
            });

            setData(response.data.data || []);

            setPagination({
                total: response.data.pagination?.Total || 0,
                totalPages: response.data.pagination?.TotalPages || 0,
            });
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            toast.error("Ocorreu um erro ao carregar os dados ❌");
            setError("Erro ao carregar os dados.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [
        periodo,
        valorPeriodo,
        dataInicio,
        dataFim,
        agruparPor,
        cidade,
        vendedor,
        origem,
        currentPage,
        perPage,
    ]);

    const handlePerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        setCurrentPage(1);
    };

    // Exportação para Excel com todos os registros
    const exportToExcel = async () => {
        try {
            setLoading(true);

            // Recupera os parâmetros atuais dos filtros, sem paginação
            const params = getReportParams(false);

            // Consulta todos os registros
            const response = await api.get(
                "/v1/relatorio/pedido/pedidocompleto",
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${accessKey}`,
                    },
                }
            );

            // Obtém os dados retornados pela API
            const dadosCompletos = response.data.data || response.data;

            if (!Array.isArray(dadosCompletos) || dadosCompletos.length === 0) {
                toast.error("Não existem dados para exportar.");
                return;
            }

            // Cria a planilha
            const worksheet = XLSX.utils.json_to_sheet(dadosCompletos);

            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Relatório de Vendas"
            );

            // Gera o arquivo Excel
            XLSX.writeFile(
                workbook,
                "Relatorio_Vendas.xlsx"
            );

            toast.success("Excel gerado com sucesso! 📊");

        } catch (error) {
            console.error("Erro ao exportar Excel:", error);
            toast.error("Erro ao gerar arquivo Excel.");
        } finally {
            setLoading(false);
        }
    };

    // Impressão do relatório
    const imprimir = async () => {
        setLoading(true);

        try {
            const params = {
                ...getReportParams(false),
                usuario,
            };

            const response = await api.get(
                "/v1/impressoes/relatorio/pedido",
                {
                    responseType: "arraybuffer",
                    params,
                    headers: {
                        Authorization: `Bearer ${accessKey}`,
                    },
                }
            );

            const blob = new Blob([response.data], {
                type: "application/pdf",
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.target = "_blank";
            link.click();

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Erro ao imprimir:", error);
            toast.error("Erro ao gerar PDF ❌");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-container">
            <Sidebar />

            <main className="content-container">
                <div
                    className="container-fluid py-4 d-flex flex-column"
                    style={{ minHeight: "100vh" }}
                >
                    <Title
                        text="Relatório de Vendas"
                        icon="shopping-cart"
                    />

                    <Description text="Relatório com filtros de período, agrupamento, cidade e vendedor" />

                    {/* FILTROS */}
                    <div className="card p-3 mb-3 shadow-sm">
                        <div className="row g-3">
                            {/* Período */}
                            <div className="col-md-2">
                                <label className="form-label">
                                    Período
                                </label>

                                <select
                                    className="form-select"
                                    value={periodo}
                                    onChange={(e) => {
                                        setPeriodo(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option>Do Dia</option>
                                    <option>Do Mês</option>
                                    <option>De um período</option>
                                </select>
                            </div>

                            {/* Data */}
                            {periodo === "Do Dia" && (
                                <div className="col-md-2">
                                    <label className="form-label">
                                        Data
                                    </label>

                                    <input
                                        type="date"
                                        className="form-control"
                                        value={valorPeriodo}
                                        onChange={(e) => {
                                            setValorPeriodo(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            )}

                            {/* Mês */}
                            {periodo === "Do Mês" && (
                                <div className="col-md-2">
                                    <label className="form-label">
                                        Mês
                                    </label>

                                    <input
                                        type="month"
                                        className="form-control"
                                        value={valorPeriodo.slice(0, 7)}
                                        onChange={(e) => {
                                            setValorPeriodo(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            )}

                            {/* Intervalo de datas */}
                            {periodo === "De um período" && (
                                <>
                                    <div className="col-md-2">
                                        <label className="form-label">
                                            Início
                                        </label>

                                        <input
                                            type="date"
                                            className="form-control"
                                            value={dataInicio}
                                            onChange={(e) => {
                                                setDataInicio(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label">
                                            Fim
                                        </label>

                                        <input
                                            type="date"
                                            className="form-control"
                                            value={dataFim}
                                            onChange={(e) => {
                                                setDataFim(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Agrupar por */}
                            <div className="col-md-2">
                                <label className="form-label">
                                    Agrupar por
                                </label>

                                <select
                                    className="form-select"
                                    value={agruparPor}
                                    onChange={(e) => {
                                        const novoAgrupamento =
                                            e.target.value;

                                        setAgruparPor(novoAgrupamento);
                                        setCurrentPage(1);

                                        if (novoAgrupamento !== "Produto") {
                                            setOrigem("T");
                                        }
                                    }}
                                >
                                    <option>Cliente</option>
                                    <option>Produto</option>
                                    <option>Vendedor</option>
                                    <option>Loja</option>
                                </select>
                            </div>

                            {/* NOVO FILTRO: ORIGEM */}
                            {agruparPor === "Produto" && (
                                <div className="col-md-2">
                                    <label className="form-label">
                                        Origem
                                    </label>

                                    <select
                                        className="form-select"
                                        value={origem}
                                        onChange={(e) => {
                                            setOrigem(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="T">Todos</option>
                                        <option value="P">
                                            Pronta Entrega
                                        </option>
                                        <option value="E">
                                            Encomenda
                                        </option>
                                    </select>
                                </div>
                            )}

                            {/* Cidade */}
                            <div className="col-md-2">
                                <label className="form-label">
                                    Cidade
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    value={cidade}
                                    onChange={(e) => {
                                        setCidade(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Ex: Juazeiro do Norte"
                                />
                            </div>

                            {/* Vendedor */}
                            <div className="col-md-2">
                                <label className="form-label">
                                    Vendedor
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    value={vendedor}
                                    onChange={(e) => {
                                        setVendedor(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Nome do vendedor"
                                />
                            </div>
                        </div>

                        {/* BOTÕES */}
                        <div className="mt-3 d-flex justify-content-end gap-2">
                            <button
                                disabled={!podeImprimir("/rel/relpedido")}
                                className="btn btn-primary d-flex align-items-center gap-2"
                                onClick={imprimir}
                            >
                                <img
                                    src={Impressora}
                                    alt="Imprimir"
                                    width="20"
                                />
                                Imprimir
                            </button>

                            <button
                                disabled={!podeImprimir("/rel/relpedido")}
                                className="btn btn-outline-success d-flex align-items-center gap-2"
                                onClick={exportToExcel}
                            >
                                <img
                                    src={ImgXLS}
                                    alt="Excel"
                                    width="20"
                                />
                                Excel
                            </button>
                        </div>
                    </div>

                    {/* ERROS */}
                    {error && (
                        <div
                            className="alert alert-danger"
                            role="alert"
                        >
                            {error}
                        </div>
                    )}

                    {/* TABELA */}
                    <div className="flex-grow-1 d-flex flex-column">
                        {loading ? (
                            <div className="text-center py-4">
                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                >
                                    <span className="visually-hidden">
                                        Carregando...
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-wrapper flex-grow-1 d-flex flex-column">
                                <Table
                                    columns={columns}
                                    data={data}
                                    showFooter={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* PAGINAÇÃO */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={setCurrentPage}
                        perPage={perPage}
                        onPerPageChange={handlePerPageChange}
                        total={pagination.total}
                    />
                </div>
            </main>
        </div>
    );
};

export default RelPedidos;
